<template>
    <div class="page-vzdelavanie bg-base">
        <div class="container space-y-8">
            <!-- No overflow-hidden here: it would clip the filter dropdowns -->
            <section class="relative">
                <div class="mx-auto max-w-4xl py-8 text-center md:py-12">
                    <h1 class="mb-4 text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
                        Vzdelávanie: workshopy, kurzy a podujatia
                    </h1>
                    <p class="mx-auto mb-8 max-w-2xl text-xl text-gray-600 dark:text-gray-300">
                        Vďaka workshopom, kurzom, podujatiam či študijným a štipendijným príležitostiam môžeš nielen
                        rásť, ale aj spoznať zaujímavé organizácie a inšpiratívnych lektorov.
                    </p>
                    <div class="mb-6 flex flex-wrap items-center justify-center gap-3">
                        <CButton to="/profile/skills/create?type=kurz" variant="primary-outline" aria-label="Vytvoriť vlastný kurz">Vytvoriť vlastný kurz</CButton>
                        <span class="text-sm text-txt-body">Zdieľaj svoje skúsenosti v lekciách.</span>
                    </div>
                    <div class="mx-auto max-w-3xl text-left">
                        <nav class="mb-4 flex flex-wrap justify-center gap-2" aria-label="Typ vzdelávania">
                            <NuxtLink
                                v-for="category in learningCategories"
                                :key="category.value"
                                :to="categoryRoute(category.value)"
                                :aria-current="isCategorySelected(category.value) ? 'page' : undefined"
                                class="rounded-full border px-4 py-2 text-sm font-medium transition-colors"
                                :class="isCategorySelected(category.value) ? 'border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200' : 'border-default text-txt-body hover:bg-subtle'"
                            >{{ category.label }}</NuxtLink>
                        </nav>
                        <ModernSkillsFilter :value="filters" :forced-filters="{}" @input="updateFilters">
                            <div class="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:w-auto sm:justify-start">
                                <FilterSortTabs
                                    :model-value="sortMode"
                                    :options="skillSortOptions"
                                    @update:model-value="setSortMode"
                                />
                                <CViewToggle v-model="viewMode" />
                            </div>
                        </ModernSkillsFilter>
                        <div v-if="searchResults" class="mt-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                            {{ searchResults.nbHits }} výsledkov
                        </div>
                    </div>
                </div>
            </section>

            <!-- Independent v-ifs, not a v-else chain: the grid stays mounted while results are
                 re-fetched after hydration (docs/FILTER_LISTING_PATTERNS.md #6) -->
            <CardRowLoadingIndicator v-if="isSearching && !searchResults?.hits?.length" />

            <div v-if="searchResults?.hits?.length" class="bg-white pb-12 dark:bg-surface-card">
                <!-- Cards View (Masonry) -->
                <CMasonryGrid
                    v-if="viewMode === 'cards'"
                    :items="searchResultsWithBanner"
                    :column-width="280"
                    :gap="24"
                    :ssr-columns="3"
                    mode="masonry"
                >
                    <template #default="{ item: skill }">
                        <CMasonryNetworkBannerCard v-if="skill._isBanner" />
                        <CMasonryCard
                            v-else
                            :image-link="skill.image || skill.coverImg || defaultImage"
                            :toasts="getToasts(skill)"
                            :toast-type="getToastTypes(skill)"
                            :title="skill.title"
                            :subscript="getSkillTypeName(skill)"
                            :attributes="getAttributes(skill)"
                            :attribute-icon-names="getAttributeIcons(skill)"
                            :tags="skill.topics || []"
                            :page="'vzdelavanie-slug'"
                            :page-params="{ slug: skill.slug || skill.id }"
                            @tag-click="handleTagClick"
                        />
                    </template>
                </CMasonryGrid>

                <!-- Rows View (List) -->
                <div v-else class="space-y-3">
                    <SkillRow v-for="skill in searchResults.hits" :key="skill.id || skill.objectID" :skill="skill" />
                </div>

                <!-- Pagination. "Ďalšia strana" is a real link to ?page=N, so crawlers reach page 2+ -->
                <div v-if="searchResults.nbPages > 1" class="mt-8 flex flex-wrap items-center justify-center gap-4">
                    <CButton
                        v-if="page < searchResults.nbPages"
                        :disabled="isSearching"
                        :to="listingPageRoute(route.query, page + 1)"
                        variant="primary"
                        @click="scrollToResultsTop"
                    >
                        Ďalšia strana
                    </CButton>
                    <Paging :pages="searchResults.nbPages" :value="page" @input="changePage" />
                </div>
            </div>

            <NothingFound v-if="!isSearching && !searchResults?.hits?.length" :search-term="textNotFound" />

            <div class="space-y-10 pb-4">
                <ListingSeoLinks
                    title="Workshopy, kurzy a ďalšie vzdelávanie"
                    description="Organizácie na GROWNi zverejňujú workshopy, webináre a konferencie, kurzy aj štipendiá. Vyber si typ, ktorý hľadáš."
                    :links="educationTypeLinks"
                />
                <ListingSeoLinks title="Ďalšie príležitosti na GROWNi" :links="educationRelatedLinks" :bordered="false" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { SkillType } from '#shared/types/Skill'
import { skillTypeDict } from '#shared/types/dictionary'
import { SkillFilterSortByType } from '#shared/types/FilterItem'
import { useSkills } from '@/composables/skills/useSkills'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { usePersonalizationQuery } from '@/composables/search/usePersonalizationQuery'
import CMasonryCard from '@/components/ui/CMasonryCard.vue'
import CMasonryNetworkBannerCard from '@/components/ui/CMasonryNetworkBannerCard.vue'
import CMasonryGrid from '@/components/ui/CMasonryGrid.vue'
import CViewToggle from '@/components/ui/CViewToggle.vue'
import type { ViewMode } from '@/components/ui/CViewToggle.vue'
import FilterSortTabs from '@/components/filters/components/FilterSortTabs.vue'
import SkillRow from '@/components/skills/SkillRow.vue'
import Paging from '@/components/dashboard/Paging.vue'
import ListingSeoLinks from '@/components/common/ListingSeoLinks.vue'
import { educationRelatedLinks, educationTypeLinks } from '@/constants/listingSeoLinks'
import { listingPageRoute, useListingPaginationHead } from '@/composables/head/useListingPaginationHead'
import coursesIcon from '@/assets/icons/courses.svg'
// Imported rather than `new URL(..., import.meta.url)`: that resolves to a file:// path during
// SSR, which broke the placeholder image and the hydration of the server-rendered cards
const defaultImage = coursesIcon

// Composables
const route = useRoute()
const router = useRouter()
const applicationStore = useApplicationStore()

// Head meta — the page shipped without any <title> or description before this
useHeadMeta()
const { searchSkills, searchSkillsPersonalized } = useSkills()
const { serializeFilters } = useTypesense()
const { getSkillQuery } = usePersonalizationQuery()

const isAuthenticated = computed(() => !!applicationStore.userId)

const learningCategories = [
    { value: '', label: 'Všetko' },
    { value: SkillType.kurz, label: 'Kurzy vlastným tempom' },
    { value: SkillType.event, label: 'Podujatia a workshopy' },
    { value: SkillType.workgroup, label: 'Skupiny' },
    { value: SkillType.studyOpportunity, label: 'Štúdium a štipendiá' }
]

function isCategorySelected(type: string) {
    const selected = Array.isArray(route.query.type) ? route.query.type : route.query.type ? [route.query.type] : []
    return type ? selected.length === 1 && selected[0] === type : selected.length === 0
}

function categoryRoute(type: string) {
    return {
        path: '/vzdelavanie',
        query: { ...route.query, type: type || undefined, page: undefined, dateRange: undefined, dateStart: undefined, dateEnd: undefined }
    }
}

type SortMode = 'recommended' | 'upcoming'

const skillSortOptions = [
    { value: 'recommended', label: 'Odporúčané', shortLabel: 'Tip' },
    { value: 'upcoming', label: 'Najbližšie termíny', shortLabel: 'Termín' }
]

const defaultSortMode: SortMode = 'recommended'

const sortMode = computed<SortMode>(() => {
    const requestedSort = route.query.sort
    if (requestedSort === 'recommended' || requestedSort === 'upcoming') {
        return requestedSort
    }
    return defaultSortMode
})

const effectiveSortBy = computed(() => {
    if (sortMode.value === 'upcoming') return SkillFilterSortByType.UPCOMING
    return isAuthenticated.value ? SkillFilterSortByType.ALL : SkillFilterSortByType.UPCOMING
})

const isSearching = ref(true)
const textNotFound = ref('ale žiadne vzdelanie')
const searchResults = ref<any>(null)

const viewMode = ref<ViewMode>('cards')

const textSearch = computed(() => (route.query.q || '') as string)
const page = computed(() => Number(route.query.page) || 1)

useListingPaginationHead(page)

const filters = computed(() => {
    const result: any = {}

    // Only include non-empty values
    if (route.query.q) {
        result.q = route.query.q
    }

    // Note: sortBy is now auth-based, not from URL

    // Date range filters
    if (route.query.dateRange) {
        result.dateRange = route.query.dateRange
    }

    if (route.query.dateStart) {
        result.dateStart = Number(route.query.dateStart)
    }

    if (route.query.dateEnd) {
        result.dateEnd = Number(route.query.dateEnd)
    }

    // Handle array parameters - only include if they have values
    const topicsArray = Array.isArray(route.query.topics)
        ? route.query.topics
        : route.query.topics
          ? [route.query.topics]
          : []
    if (topicsArray.length > 0) {
        result.topics = topicsArray
    }

    const typeArray = Array.isArray(route.query.type) ? route.query.type : route.query.type ? [route.query.type] : []
    if (typeArray.length > 0) {
        result.type = typeArray
    }

    return result
})

const searchResultsWithBanner = computed(() => {
    if (!searchResults.value?.hits?.length) return []
    const hits = [...searchResults.value.hits]
    const bannerItem = { _isBanner: true, id: 'banner-network' }
    if (hits.length > 1) {
        hits.splice(1, 0, bannerItem)
    } else {
        hits.push(bannerItem)
    }
    return hits
})

function changePage(pageNumber: number) {
    router.push(listingPageRoute(route.query, pageNumber))
}

// The next page replaces the results, so bring the reader back up to them
function scrollToResultsTop() {
    if (import.meta.client) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
}

function updateFilters(newFilters: any) {
    // Create a clean query object with only non-empty values
    const query: any = {}

    // Add non-empty string values
    if (newFilters.q && newFilters.q.trim()) {
        query.q = newFilters.q.trim()
    }

    // Date range filters
    if (newFilters.dateRange) {
        query.dateRange = newFilters.dateRange
    }

    if (newFilters.dateStart) {
        query.dateStart = newFilters.dateStart
    }

    if (newFilters.dateEnd) {
        query.dateEnd = newFilters.dateEnd
    }

    // Add non-empty array values
    if (newFilters.topics && Array.isArray(newFilters.topics) && newFilters.topics.length > 0) {
        query.topics = newFilters.topics
    }

    if (newFilters.type && Array.isArray(newFilters.type) && newFilters.type.length > 0) {
        query.type = newFilters.type
    }

    if (sortMode.value !== defaultSortMode) {
        query.sort = sortMode.value
    }

    router.push({
        name: route.name!,
        query
    })
}

function setSortMode(mode: string) {
    const nextMode = mode as SortMode
    if (nextMode === sortMode.value) return

    router.push({
        name: route.name!,
        query: {
            ...route.query,
            sort: nextMode === defaultSortMode ? undefined : nextMode,
            page: undefined
        }
    })
}

// Helper functions for CMasonryCard
function getToasts(skill: any) {
    const toasts = []
    if (skill.type === SkillType.workgroup) {
        toasts.push('Prihlás sa')
    }
    return toasts
}

function getToastTypes(skill: any) {
    const types = []
    if (skill.type === SkillType.workgroup) {
        types.push('warning')
    }
    return types
}

function getSkillTypeName(skill: any) {
    if (!skill.type) return ''
    return skillTypeDict[skill.type as SkillType] || ''
}

function getAttributes(skill: any) {
    const attributes = []

    // Add start date for events (not for kurz - those are recordings without expiration)
    const isEvent = skill.type === SkillType.event || skill.type === SkillType.studyOpportunity

    if (isEvent && skill.startDate) {
        attributes.push(formatDate(skill.startDate))
    }

    // Add location for physical events
    if (isEvent && !skill.isOnline && skill.city) {
        attributes.push(skill.city)
    }

    // Add online indicator
    if (isEvent && skill.isOnline) {
        attributes.push('Online')
    }

    return attributes
}

function getAttributeIcons(skill: any) {
    const icons = []

    const isEvent = skill.type === SkillType.event || skill.type === SkillType.studyOpportunity

    if (isEvent && skill.startDate) {
        icons.push('calendar')
    }

    if (isEvent && !skill.isOnline && skill.city) {
        icons.push('location-outline')
    }

    if (isEvent && skill.isOnline) {
        icons.push('globe')
    }

    return icons
}

function formatDate(dateString: string) {
    // Simple date formatting - you might want to use dayjs here
    return new Date(dateString).toLocaleDateString('sk-SK')
}

function handleTagClick(tag: string) {
    router.push({
        name: 'vzdelavanie',
        query: { topics: tag }
    })
}

/** Runs the search for the current route; shared by the SSR fetch and client re-searches. */
async function fetchSkills() {
    // Build filters from current filter state - they are already properly filtered by the computed property
    const filtersObj: Record<string, any> = {}

    // Add filters that are already validated in the computed property
    if (filters.value.topics) {
        filtersObj.topics = filters.value.topics
    }

    if (filters.value.type) {
        filtersObj.type = filters.value.type
    }

    // Serialize filters for Typesense
    const filtersString = serializeFilters(filtersObj)

    // Use auth-based sorting
    const sortBy = effectiveSortBy.value

    // Build date filter for Typesense
    let dateFilter = ''
    const selectedTypes = filters.value.type || []
    const onlyKurzSelected = selectedTypes.length === 1 && selectedTypes[0] === SkillType.kurz

    if (filters.value.dateStart && filters.value.dateEnd) {
        // User explicitly set date range
        dateFilter = `startDate:>=${filters.value.dateStart} && startDate:<=${filters.value.dateEnd}`
    } else if (!onlyKurzSelected) {
        // Default: hide past events/studyOpportunity, but keep kurz (recordings) visible
        // Skip this filter if user explicitly selected only kurz (kurz has no date expiration)
        const nowTimestamp = Date.now()
        dateFilter = `(type:=kurz || startDate:>=${nowTimestamp})`
    }

    // Combine filters
    let combinedFilters = filtersString
    if (dateFilter) {
        combinedFilters = combinedFilters ? `${combinedFilters} && ${dateFilter}` : dateFilter
    }

    // Search options - 24 is divisible by 4, 3, and 2 columns
    const searchOptions = {
        page: page.value - 1, // 0-based pages
        hitsPerPage: 24,
        ...(combinedFilters && { filterBy: combinedFilters })
    }

    // Use personalized search when authenticated with no text search (using cached query)
    const cachedPersonalizationQuery = getSkillQuery.value
    const shouldUsePersonalized = isAuthenticated.value && !textSearch.value && cachedPersonalizationQuery

    let results
    if (shouldUsePersonalized) {
        results = await searchSkillsPersonalized(
            textSearch.value,
            {
                ...searchOptions,
                personalizationQuery: cachedPersonalizationQuery
            },
            sortBy
        )
    } else if (textSearch.value) {
        // Use personalized (hybrid) search for typed queries too
        results = await searchSkillsPersonalized(textSearch.value, searchOptions, sortBy)
    } else {
        // Non-authenticated with no search: show upcoming
        results = await searchSkills(textSearch.value, searchOptions, sortBy)
    }

    // Transform results to match PaginatedCardGrid expectations
    return {
        hits: results.hits,
        nbHits: results.nbHits,
        nbPages: results.nbPages,
        page: results.page,
        hitsPerPage: results.hitsPerPage,
        pagedHits: {
            [results.page]: results.hits
        }
    }
}

async function searchSkillsFunction() {
    isSearching.value = true

    try {
        searchResults.value = await fetchSkills()
    } catch (error) {
        console.error('Error searching skills:', error)
        // Set empty results on error
        searchResults.value = {
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            hitsPerPage: 24,
            pagedHits: {}
        }
    } finally {
        isSearching.value = false
    }
}

// Initial results render on the server, so crawlers get the listing and its links to the
// detail pages (the search used to start only on the client). SSR has no user, so this is
// the anonymous result set; signed-in users get theirs re-fetched in onMounted.
const { data: initialSkills } = await useAsyncData(`skills-search-${JSON.stringify(route.query)}`, () =>
    fetchSkills().catch((error) => {
        console.error('Error searching skills:', error)
        return null
    })
)

if (initialSkills.value) {
    searchResults.value = initialSkills.value
    isSearching.value = false
}

watch([() => route.query, filters], () => {
    searchSkillsFunction()
})

onMounted(() => {
    if (textSearch.value) {
        textNotFound.value = textSearch.value
    }
    // Sorting and personalization depend on the signed-in user, which SSR does not know
    if (!initialSkills.value || isAuthenticated.value) {
        searchSkillsFunction()
    }
    scrollToCardFromSession()
})

definePageMeta({
    title: 'Vzdelávanie'
})
</script>
